<template>
  <Loading v-if="loading" />
  <template v-else-if="seminar">
    <Heading1>Upravit seminář</Heading1>
    <SeminarForm
      :is-submitting="isSubmitting"
      :seminar="seminar"
      @submit="onSubmit"
    ></SeminarForm>
  </template>
</template>

<script lang="ts" setup>
import type { SeminarFormValues } from '@/components/seminar/SeminarForm/seminar-form';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Loading from '@/components/common/Loading.vue';
import Heading1 from '@/components/common/heading1.vue';
import SeminarForm from '@/components/seminar/SeminarForm/SeminarForm.vue';
import {
  convertFromQuasarDate,
  convertToQuasarDate,
} from '@/components/seminar/translators/date';
import { useQuasar } from 'quasar';
import {
  type GetSeminarsQueryVariables,
  SeminarType,
} from '@/api/graphql/codegen/graphql';
import { useGraphQLMutation, useGraphQLQuery } from '@/api/graphql/adapters';
import { GetSeminarsQueryDocument } from '@/api/graphql/documents/GetSeminars';
import { UpdateSeminarMutationDocument } from '@/api/graphql/documents/UpdateSeminar';
import { useQueryClient } from '@tanstack/vue-query';

const $q = useQuasar();

const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();

const seminarId = computed(() => Number(route.params.id));
const getSeminarsQueryVariables = computed<GetSeminarsQueryVariables>(() => ({
  seminarsInput: { seminarId: seminarId.value },
}));

const {
  data: result,
  isLoading: loading,
  queryKey,
} = useGraphQLQuery(GetSeminarsQueryDocument, getSeminarsQueryVariables);
const seminar = computed<SeminarFormValues | null>(() => {
  if (!result.value) {
    return null;
  }

  const foundSeminar = result.value.seminars[0];

  const converted = {
    identifier: foundSeminar.identifier,
    name: foundSeminar.name,
    date: convertToQuasarDate(new Date(foundSeminar.date)),
    endDate: foundSeminar.endDate
      ? convertToQuasarDate(new Date(foundSeminar.endDate))
      : undefined,
    lector: foundSeminar.lector ?? undefined,
    accreditation: foundSeminar.accreditation ?? '',
    duration: foundSeminar.duration ?? undefined,
    city: foundSeminar.city,
    price: foundSeminar.price ?? undefined,
    type: foundSeminar.type,
    capacity: foundSeminar.capacity ? String(foundSeminar.capacity) : undefined,
    theoryDuration: foundSeminar.theoryDuration ?? undefined,
    practiceDuration: foundSeminar.practiceDuration ?? undefined,
    substituteCapacity: foundSeminar.substituteCapacity
      ? String(foundSeminar.substituteCapacity)
      : undefined,
    deposit: foundSeminar.deposit ?? undefined,
  } satisfies SeminarFormValues;

  return converted;
});

const { mutateAsync, isPending: isSubmitting } = useGraphQLMutation(
  UpdateSeminarMutationDocument,
  {
    onError: (err: any) => {
      $q.notify({
        color: 'negative',
        message: err?.message ?? 'Nepodařilo se aktualizovat seminář.',
        progress: true,
      });
    },
  },
);

const onSubmit = async (values: SeminarFormValues) => {
  const date = convertFromQuasarDate(values.date).toISOString();
  const endDate =
    values.endDate === ''
      ? null
      : values.endDate
      ? convertFromQuasarDate(values.endDate).toISOString()
      : undefined;
  const result = await mutateAsync({
    seminarInput: {
      ...values,
      date: date,
      endDate: endDate,
      id: seminarId.value,
      price: values.type === SeminarType.Idz ? '0' : values.price,
      capacity: Number(values.capacity) || null,
      substituteCapacity: Number(values.substituteCapacity) || null,
      deposit: values.deposit || null,
      theoryDuration: values.theoryDuration || null,
      practiceDuration: values.practiceDuration || null,
    },
  });
  await Promise.all([
    queryClient.invalidateQueries({ queryKey }),
    router.push(`/seminar/${result?.updateSeminar.id}`),
  ]);
};
</script>
